import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  ChevronDown, ChevronRight, Folder, MessageSquare, FolderIcon
} from 'lucide-react';
import { WorkItem } from '../../types';
import { GroupedWorkItem } from '../../types/table';
import { CategoryMenu } from './CategoryMenu';
import { SubcategoryMenu } from './SubcategoryMenu';
import { ItemMenu } from './ItemMenu';
import { TagsCell, NotesCell, StatusCell } from '../InlineEdit';
import { InlineEditTags } from '../InlineEdit/InlineEditTags';
import { InlineEditNotes } from '../InlineEdit/InlineEditNotes';
import { InlineEditStatus } from '../InlineEdit/InlineEditStatus';
import { styleDefinitions as styles } from '../../theme/styles';
import { db } from '../../services/database';

interface ColumnConfig {
  onDelete: (id: string) => void;
  onAdd: (parentItem?: WorkItem) => void;
  onUpdate: (item: WorkItem) => void;
  openCategoryMenu: string | null;
  setOpenCategoryMenu: (id: string | null) => void;
  openSubcategoryMenu: string | null;
  setOpenSubcategoryMenu: (id: string | null) => void;
  openItemMenu: string | null;
  setOpenItemMenu: (id: string | null) => void;
  setEditingItem: (item: WorkItem | null) => void;
  setCommentingItem: (item: WorkItem | null) => void;
  data: WorkItem[];
  setSubcategoryModal?: (state: { isOpen: boolean; categoryName: string; onSave: (name: string) => void }) => void;
  triggerRefresh?: () => void;
}

export const createTreeColumns = ({
  onDelete,
  onAdd,
  onUpdate,
  openCategoryMenu,
  setOpenCategoryMenu,
  openSubcategoryMenu,
  setOpenSubcategoryMenu,
  openItemMenu,
  setOpenItemMenu,
  setEditingItem,
  setCommentingItem,
  data,
  setSubcategoryModal,
  triggerRefresh
}: ColumnConfig): ColumnDef<GroupedWorkItem>[] => [
  {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => {
      if (!row.getCanExpand()) return null;
      
      return (
        <button
          onClick={row.getToggleExpandedHandler()}
          style={styles.table.expandIconButton}
        >
          {row.getIsExpanded() ? (
            <ChevronDown size={16} style={styles.table.expandIcon} />
          ) : (
            <ChevronRight size={16} style={styles.table.expandIcon} />
          )}
        </button>
      );
    },
  },
  {
    id: 'content',
    header: 'Items',
    size: 400,
    minSize: 400,
    maxSize: 400,
    cell: ({ row }) => {
      const level = row.original.level || 0;
      
      if (level === 1) {

        return (
          <div style={styles.table.categoryCard}>
            <div style={styles.table.categoryCardContent}>
              <div style={styles.table.categoryIcon}>
                <FolderIcon size={18} style={styles.table.categoryIconSvg} />
              </div>
              <div style={styles.table.categoryInfo}>
                <h3 style={styles.table.categoryTitle}>{row.original.level1}</h3>
              </div>
            </div>
          </div>
        );
      }
      
      if (level === 2) {
        return (
          <div style={styles.table.subcategoryRow}>
            <span style={styles.table.subcategoryText}>
              <Folder size={16} style={styles.table.subcategoryIcon} />
              {row.original.level2}
            </span>
          </div>
        );
      }
      
      if (level === 3) {
        const hasSubcategory = row.original.level2 && row.original.level2 !== 'Items';
        const itemRowStyle = hasSubcategory
          ? styles.table.itemRowWithSubcategory
          : styles.table.itemRowWithoutSubcategory;

        return (
          <div style={itemRowStyle}>
            <div style={styles.table.itemContent}>
              <div style={styles.table.itemInfo}>
                {row.original.link ? (
                  <a
                    href={row.original.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.table.itemLink}
                    title="Open link"
                  >
                    {row.original.fileName}
                  </a>
                ) : (
                  <span style={styles.table.itemInfo}>
                    {row.original.fileName}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }
      
      return null;
    },
  },
  {
    id: 'tags',
    header: () => <span style={styles.table.tagHeaderCell}>Tags</span>,
    cell: ({ row }) => {
      if (row.original.level === 3) {
        return (
          <TagsCell
            item={row.original as WorkItem}
            onUpdate={onUpdate}
            savedTags={data.reduce((acc: string[], item) => {
              if (item.tags) {
                item.tags.forEach(tag => {
                  if (!acc.includes(tag)) acc.push(tag);
                });
              }
              return acc;
            }, [])}
          />
        );
      }
      return null;
    },
  },
  {
    id: 'notes',
    header: () => <span style={styles.table.notesHeaderCell}>Notes</span>,
    cell: ({ row }) => {
      if (row.original.level === 3) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <NotesCell
              item={row.original as WorkItem}
              onUpdate={onUpdate}
            />
            {row.original.comments.length > 0 && (
              <button
                onClick={() => setCommentingItem(row.original as WorkItem)}
                style={styles.table.commentButton}
              >
                <MessageSquare size={10} />
                <span>{row.original.comments.length}</span>
              </button>
            )}
          </div>
        );
      }
      return null;
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      if (row.original.level === 3) {
        return (
          <StatusCell
            item={row.original as WorkItem}
            onUpdate={onUpdate}
          />
        );
      }
      return null;
    }},
  {
    id: 'actions',
    header: () => 'Actions',
    cell: ({ row }) => {
      if (row.original.level === 3) {
        const isMenuOpen = openItemMenu === row.original.id;
        
        return (
          <div style={styles.table.actionCellWrapper}>
            <ItemMenu
              item={row.original as WorkItem}
              isOpen={isMenuOpen}
              onToggle={() => setOpenItemMenu(isMenuOpen ? null : row.original.id)}
              onClose={() => setOpenItemMenu(null)}
              onEdit={() => setEditingItem(row.original as WorkItem)}
              onDelete={() => onDelete(row.original.id)}
              onComment={() => setCommentingItem(row.original as WorkItem)}
            />
          </div>
        );
      }
      
      if (row.original.isGroupHeader) {
        if (row.original.level === 1) {
          const isMenuOpen = openCategoryMenu === row.original.id;

          return (
            <div style={styles.table.actionCellWrapper}>
              <CategoryMenu
                categoryId={row.original.id}
                categoryName={row.original.level1}
              isOpen={isMenuOpen}
              onToggle={() => setOpenCategoryMenu(isMenuOpen ? null : row.original.id)}
              onClose={() => setOpenCategoryMenu(null)}
              onAddItem={() => onAdd(row.original as WorkItem)}
              onRename={(newName) => {
                const savedCategories = localStorage.getItem('cleanRoomCategories');
                if (savedCategories) {
                  try {
                    const parsed = JSON.parse(savedCategories);
                    const index = parsed.indexOf(row.original.level1);
                    if (index > -1) {
                      parsed[index] = newName;
                      localStorage.setItem('cleanRoomCategories', JSON.stringify(parsed));
                      data.filter(item => item.level1 === row.original.level1).forEach(item => {
                        onUpdate({ ...item, level1: newName });
                      });
                    }
                  } catch (e) {
                    console.error('Failed to update category name');
                  }
                }
              }}
              onAddSubcategory={(subcategoryName) => {
                if (setSubcategoryModal) {
                  setSubcategoryModal({
                    isOpen: true,
                    categoryName: row.original.level1,
                    onSave: (subcategoryName) => {
                      // Check if subcategory already exists
                      const existingSubcategory = data.find(
                        item => item.level1 === row.original.level1 && item.level2 === subcategoryName
                      );
                      
                      if (existingSubcategory) {
                        return; // Modal will handle this validation
                      }
                      
                      // Save the subcategory name to localStorage to make it available
                      const savedSubcategories = localStorage.getItem('cleanRoomSubcategories') || '{}';
                      const subcategories = JSON.parse(savedSubcategories);
                      
                      if (!subcategories[row.original.level1]) {
                        subcategories[row.original.level1] = [];
                      }
                      
                      if (!subcategories[row.original.level1].includes(subcategoryName)) {
                        subcategories[row.original.level1].push(subcategoryName);
                        localStorage.setItem('cleanRoomSubcategories', JSON.stringify(subcategories));
                      }
                      
                      // Subcategory is now created and will appear when the first item is added to it
                      // No automatic item creation or edit modal opening
                    }
                  });
                }
              }}
              />
            </div>
          );
        } else if (row.original.level === 2) {
          // This is a subcategory (level 2)
          const isMenuOpen = openSubcategoryMenu === row.original.id;

          return (
            <div style={styles.table.actionCellWrapper}>
              <SubcategoryMenu
                subcategoryId={row.original.id}
                categoryName={row.original.level1}
                subcategoryName={row.original.level2}
              isOpen={isMenuOpen}
              onToggle={() => setOpenSubcategoryMenu(isMenuOpen ? null : row.original.id)}
              onClose={() => setOpenSubcategoryMenu(null)}
              onAddItem={() => onAdd(row.original as WorkItem)}
              onRename={(newName) => {
                // Update subcategory name in localStorage
                const savedSubcategories = localStorage.getItem('cleanRoomSubcategories') || '{}';
                const subcategories = JSON.parse(savedSubcategories);
                
                if (subcategories[row.original.level1]) {
                  const index = subcategories[row.original.level1].indexOf(row.original.level2);
                  if (index > -1) {
                    subcategories[row.original.level1][index] = newName;
                    localStorage.setItem('cleanRoomSubcategories', JSON.stringify(subcategories));
                  }
                }
                
                // Update all items in this subcategory
                data.filter(item => 
                  item.level1 === row.original.level1 && item.level2 === row.original.level2
                ).forEach(item => {
                  onUpdate({ ...item, level2: newName });
                });
                
                // Trigger refresh to show updated subcategory
                if (triggerRefresh) {
                  triggerRefresh();
                }
              }}
              onDelete={() => {
                // Remove subcategory from localStorage
                const savedSubcategories = localStorage.getItem('cleanRoomSubcategories') || '{}';
                const subcategories = JSON.parse(savedSubcategories);
                
                if (subcategories[row.original.level1]) {
                  const index = subcategories[row.original.level1].indexOf(row.original.level2);
                  if (index > -1) {
                    subcategories[row.original.level1].splice(index, 1);
                    localStorage.setItem('cleanRoomSubcategories', JSON.stringify(subcategories));
                  }
                }
                
                // Move all items from this subcategory to the parent category (level2 = 'Items')
                data.filter(item => 
                  item.level1 === row.original.level1 && item.level2 === row.original.level2
                ).forEach(item => {
                  onUpdate({ ...item, level2: 'Items' });
                });
                
                // Trigger refresh to remove deleted subcategory from view
                if (triggerRefresh) {
                  triggerRefresh();
                }
              }}
              />
            </div>
          );
        }
      }

      return <div style={styles.table.actionMenuWrapper}></div>;
    }},
];

export const createTableColumns = (
  onDelete: (id: string) => void,
  openItemMenu: string | null,
  setOpenItemMenu: (id: string | null) => void,
  setEditingItem: (item: WorkItem | null) => void,
  setCommentingItem: (item: WorkItem | null) => void,
  onUpdate?: (item: WorkItem) => void,
  data?: WorkItem[]
): ColumnDef<WorkItem>[] => [
  {
    id: 'fileName',
    header: () => 'Item Name',
    accessorKey: 'fileName',
    cell: ({ row }) => (
      row.original.link ? (
        <a
          href={row.original.link}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.table.linkText}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#3b82f6';
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#000000';
            e.currentTarget.style.textDecoration = 'none';
          }}
          title="Open link"
        >
          {row.original.fileName}
        </a>
      ) : (
        <div style={styles.table.regularText}>{row.original.fileName}</div>
      )
    )},
  {
    id: 'level1',
    header: () => 'Category',
    accessorKey: 'level1',
    cell: ({ row }) => (
      <span style={styles.table.categoryBadge}>
        {row.original.level1}
      </span>
    )},
  {
    id: 'level2',
    header: () => 'Subcategory',
    accessorKey: 'level2',
    cell: ({ row }) => (
      <span style={styles.table.subcategoryText}>
        {row.original.level2 === 'Items' ? '-' : row.original.level2}
      </span>
    )},
  {
    id: 'tags',
    header: () => 'Tags',
    cell: ({ row }) => {
      if (!onUpdate || !data) {
        // Fallback to basic inline edit if no onUpdate provided
        return (
          <InlineEditTags
            tags={row.original.tags || []}
            onSave={async (newTags: string[]) => {
              const updatedItem = { ...row.original, tags: newTags };
              await db.saveWorkItem(updatedItem);
            }}
            savedTags={[]}
          />
        );
      }
      return (
        <TagsCell
          item={row.original}
          onUpdate={onUpdate}
          savedTags={data.reduce((acc: string[], item) => {
            if (item.tags) {
              item.tags.forEach(tag => {
                if (!acc.includes(tag)) acc.push(tag);
              });
            }
            return acc;
          }, [])}
        />
      );
    }},
  {
    id: 'description',
    header: () => 'Notes',
    accessorKey: 'description',
    cell: ({ row }) => {
      if (!onUpdate) {
        // Fallback to basic inline edit if no onUpdate provided
        return (
          <InlineEditNotes
            value={row.original.description || ''}
            onSave={async (newDescription: string) => {
              const updatedItem = { ...row.original, description: newDescription };
              await db.saveWorkItem(updatedItem);
            }}
          />
        );
      }
      return (
        <NotesCell
          item={row.original}
          onUpdate={onUpdate}
        />
      );
    }},
  {
    id: 'status',
    header: () => 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      if (!onUpdate) {
        // Fallback to basic inline edit if no onUpdate provided
        return (
          <InlineEditStatus
            value={row.original.status}
            onSave={async (newStatus: any) => {
              const updatedItem = { ...row.original, status: newStatus };
              await db.saveWorkItem(updatedItem);
            }}
          />
        );
      }
      return (
        <StatusCell
          item={row.original}
          onUpdate={onUpdate}
        />
      );
    },
  },
  {
    id: 'actions',
    header: () => 'Actions',
    cell: ({ row }) => {
      const isMenuOpen = openItemMenu === row.original.id;
      
      return (
        <div style={styles.table.actionCellWrapper}>
          <ItemMenu
            item={row.original}
            isOpen={isMenuOpen}
            onToggle={() => setOpenItemMenu(isMenuOpen ? null : row.original.id)}
            onClose={() => setOpenItemMenu(null)}
            onEdit={() => setEditingItem(row.original)}
            onDelete={() => onDelete(row.original.id)}
            onComment={() => setCommentingItem(row.original)}
          />
        </div>
      );
    }},
];